import { useEffect, useState } from "react";

import { motion } from "framer-motion";

import { useNavigate } from "react-router-dom";

import {

  Users2,

  Bot,

  ArrowRight,

  Sparkles,

  Wrench,

  Building2,

  CheckCircle2,

} from "lucide-react";

import Sidebar from "@/components/Sidebar";

import api from "@/lib/api";



function SettingsCard({

  index,

  icon: Icon,

  testId,

  eyebrow,

  title,

  description,

  stat,

  statLabel,

  ctaLabel,

  onClick,

}) {

  return (

    <motion.button

      type="button"

      data-testid={testId}

      initial={{ opacity: 0, y: 8 }}

      animate={{ opacity: 1, y: 0 }}

      transition={{

        duration: 0.35,

        delay: index * 0.07,

      }}

      whileHover={{ y: -3 }}

      whileTap={{ scale: 0.99 }}

      onClick={onClick}

      className="

        group

        relative

        w-full

        overflow-hidden

        rounded-[18px]
        before:absolute before:inset-x-0 before:top-0 before:h-[3px]
        before:bg-gradient-to-r before:from-[#087F78] before:via-[#D8B45A] before:to-[#B8DCD0] before:opacity-75

        border

        border-[#DCE7E1]

        bg-gradient-to-br from-white via-white to-[#F0F7F3]

        p-5

        text-left

        shadow-[0_6px_22px_rgba(11,37,40,0.055)]

        transition-all

        duration-300

        hover:border-[#B8DCD0]

        hover:shadow-[0_14px_32px_rgba(7,91,89,0.12)]

      "

    >

      {/* subtle hover glow */}

      <div

        className="

          pointer-events-none

          absolute

          -right-12

          -top-12

          h-28

          w-28

          rounded-full

          bg-[#D8B45A]/0

          blur-2xl

          transition-all

          duration-500

          group-hover:bg-[#D8B45A]/15

        "

      />



      {/* Top row */}

      <div className="relative flex items-center justify-between">

        <div className="flex items-center gap-3">

          <div

            className="

              flex

              h-10

              w-10

              items-center

              justify-center

              rounded-[12px]

              bg-gradient-to-br from-[#E1F2EC] to-[#CDE8DE]

              text-[#075B59]

              transition-all

              duration-300

              group-hover:bg-[#075B59]

              group-hover:text-white

            "

          >

            <Icon

              className="h-[18px] w-[18px]"

              strokeWidth={1.8}

            />

          </div>



          <span className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#087F78]">

            {eyebrow}

          </span>

        </div>



        <span

          className="

            flex

            h-8

            w-8

            items-center

            justify-center

            rounded-full

            border

            border-[#DCE7E1]

            text-[#82918C]

            transition-all

            duration-300

            group-hover:border-[#B8DCD0]

            group-hover:bg-gradient-to-br from-[#E1F2EC] to-[#CDE8DE]

            group-hover:text-[#075B59]

          "

        >

          <ArrowRight

            className="h-3.5 w-3.5"

            strokeWidth={1.8}

          />

        </span>

      </div>



      {/* Content */}

      <div className="relative mt-5">

        <h2 className="text-[18px] font-semibold tracking-[-0.025em] text-[#102C2D]">

          {title}

        </h2>



        <p className="mt-2 max-w-[300px] text-[10px] leading-[1.55] text-[#607572]">

          {description}

        </p>

      </div>



      {/* Bottom status */}

      <div className="relative mt-5 flex items-center justify-between border-t border-[#E9EEE9] pt-3.5">

        <div className="flex items-center gap-2">

          <span className="text-[17px] font-semibold leading-none text-[#102C2D]">

            {stat}

          </span>



          <span className="text-[9px] font-medium text-[#82918C]">

            {statLabel}

          </span>

        </div>



        <span className="text-[9px] font-semibold text-[#607572] transition-colors group-hover:text-[#075B59]">

          {ctaLabel}

        </span>

      </div>

    </motion.button>

  );

}



export default function BusinessSettings() {

  const nav = useNavigate();



  const [teamCount, setTeamCount] = useState(null);

  const [activeCount, setActiveCount] = useState(null);

  const [svcCount, setSvcCount] = useState(null);

  const [svcActiveCount, setSvcActiveCount] = useState(null);

  const [autoRules, setAutoRules] = useState(null);



  useEffect(() => {

    let cancelled = false;



    const loadSettings = async () => {

      try {

        const [{ data: team }, { data: automation }, { data: services }] =

          await Promise.all([

            api

              .get("/team")

              .catch(() => ({

                data: {

                  members: [],

                  total: 0,

                  active: 0,

                },

              })),



            api

              .get("/settings/automation")

              .catch(() => ({

                data: null,

              })),



            api

              .get("/services")

              .catch(() => ({

                data: {

                  services: [],

                  total: 0,

                  active: 0,

                },

              })),

          ]);



        if (cancelled) return;



        setTeamCount(team?.total ?? 0);

        setActiveCount(team?.active ?? 0);



        setSvcCount(services?.total ?? 0);

        setSvcActiveCount(services?.active ?? 0);



        let rules = 0;



        if (automation) {

          if (automation.appointment_reminders_enabled) {

            rules += 1;

          }



          if (automation.payment_reminders_enabled) {

            rules += 1;

          }



          if (automation.vendor_confirmations_enabled) {

            rules += 1;

          }

        }



        setAutoRules(rules);

      } catch {

        if (cancelled) return;



        setTeamCount(0);

        setActiveCount(0);

        setSvcCount(0);

        setSvcActiveCount(0);

        setAutoRules(0);

      }

    };



    loadSettings();



    return () => {

      cancelled = true;

    };

  }, []);



  return (

    <div className="min-h-screen bg-[#F7F7F2] text-[#102C2D]">

      <Sidebar />



      <main className="min-h-screen lg:pl-[250px]">

        <div className="mx-auto w-full max-w-[1120px] px-4 py-5 sm:px-6 lg:px-8">

          {/* Header */}

          <div className="mb-4 flex items-center justify-between">

         
            <div className="hidden items-center gap-2 rounded-full border border-emerald-100 bg-white px-3 py-1.5 shadow-sm sm:flex">

              <span className="relative flex h-2 w-2">

                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />

                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />

              </span>



              <span className="text-[9px] font-semibold text-[#607572]">

                Nexa AI Active

              </span>

            </div>

          </div>



          {/* Hero */}

          {/* Compact horizontal hero */}

<motion.header

  initial={{ opacity: 0, y: 6 }}

  animate={{ opacity: 1, y: 0 }}

  transition={{ duration: 0.4 }}

  className="

    relative

    mb-5

    overflow-hidden

    rounded-[16px]

    border

    border-[#174C4B]

    bg-gradient-to-br

    from-[#0B2528]

    via-[#084747]

    to-[#075B59]

    px-5

    py-4

    shadow-[0_10px_28px_rgba(11,37,40,0.14)]

    md:px-6

  "

>

  {/* Background glow */}

  <div

    className="pointer-events-none absolute rounded-full blur-3xl"

    style={{

      width: "280px",

      height: "280px",

      right: "-60px",

      top: "-100px",

      background: "rgba(92, 232, 217, 0.12)",

    }}

  />



  <div

    className="pointer-events-none absolute rounded-full blur-3xl"

    style={{

      width: "180px",

      height: "180px",

      right: "180px",

      bottom: "-100px",

      background: "rgba(49, 200, 188, 0.08)",

    }}

  />



  <div className="relative z-10 flex min-h-[108px] -translate-y-3 items-center justify-between gap-6">

    {/* Left content */}

    <div className="min-w-0 flex-1">

      <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-1">

        <Sparkles

          className="h-3 w-3 text-[#91f5eb]"

          strokeWidth={1.6}

        />



        <span className="text-[8px] font-semibold uppercase tracking-[0.15em] text-[#c4fff9]">

          Business Command Center

        </span>

      </div>



      {/* ONE-LINE HEADLINE */}

      <h2

        className="

          whitespace-nowrap

          text-[19px]

          font-semibold

          leading-none

          tracking-[-0.035em]

          text-white

          md:text-[23px]

          lg:text-[25px]

        "

      >

        Build smarter. Run smoother.{" "}

        <span className="text-[#F4C542]">One intelligent system.</span>

      </h2>



      {/* ONE-LINE DESCRIPTION */}

      <p

        className="

          mt-2

          whitespace-nowrap

          text-[9px]

          leading-none

          text-[#c3dedb]

          md:text-[10px]

        "

      >

        Configure your services, people and intelligent automations that power

        your business — while Nexa handles the repetitive work behind the

        scenes.

      </p>

    </div>



    {/* Right AI visual */}

    <div className="relative hidden h-[90px] w-[190px] shrink-0 items-center justify-center md:flex">

      {/* Glow */}

      <div

        className="absolute h-[80px] w-[80px] rounded-full blur-2xl"

        style={{

          background: "rgba(92, 232, 217, 0.20)",

        }}

      />



      {/* Connecting lines */}

      <div

        className="absolute h-px w-[55px]"

        style={{

          left: "5px",

          top: "30px",

          background: "rgba(121,238,227,0.25)",

        }}

      />



      <div

        className="absolute h-px w-[45px]"

        style={{

          right: "5px",

          top: "28px",

          background: "rgba(121,238,227,0.25)",

        }}

      />



      <div

        className="absolute h-px w-[45px]"

        style={{

          right: "5px",

          bottom: "27px",

          background: "rgba(121,238,227,0.20)",

        }}

      />



      {/* Small nodes */}

      <motion.div

        animate={{ opacity: [0.35, 1, 0.35] }}

        transition={{

          duration: 2.5,

          repeat: Infinity,

        }}

       className="

          absolute

          left-[12px]

          top-[18px]

          flex

          h-[26px]

          w-[26px]

          items-center

          justify-center

          rounded-[7px]

          border

          border-white/40

          bg-white/90

          shadow-[0_4px_12px_rgba(0,0,0,0.12)]

        "

      >

        <Wrench

          className="h-[11px] w-[11px] text-[#075B59]"

          strokeWidth={1.8}

        />

      </motion.div>



      <motion.div

  animate={{ opacity: [0.35, 1, 0.35] }}

  transition={{

    duration: 2.8,

    repeat: Infinity,

  }}

  className="

    absolute

    left-[12px]

    top-[52px]

    flex

    h-[26px]

    w-[26px]

    items-center

    justify-center

    rounded-[7px]

    border

    border-white/40

    bg-white/90

    shadow-[0_4px_12px_rgba(0,0,0,0.12)]

  "

>

  <Building2

    className="h-[11px] w-[11px] text-[#075B59]"

    strokeWidth={1.7}

  />

</motion.div>





      <motion.div

        animate={{ opacity: [1, 0.35, 1] }}

        transition={{

          duration: 2.2,

          repeat: Infinity,

        }}

        className="

          absolute

          right-[12px]

          top-[12px]

          flex

          h-[26px]

          w-[26px]

          items-center

          justify-center

          rounded-[7px]

          border

          border-white/40

          bg-white/90

          shadow-[0_4px_12px_rgba(0,0,0,0.12)]

        "

      >

        <Users2

          className="h-[11px] w-[11px] text-[#075B59]"

          strokeWidth={1.8}

        />

      </motion.div>



      <motion.div

        animate={{ opacity: [0.35, 1, 0.35] }}

        transition={{

          duration: 2.8,

          repeat: Infinity,

        }}

        className="

          absolute

          right-[10px]

          bottom-[10px]

          flex

          h-[26px]

          w-[26px]

          items-center

          justify-center

          rounded-[7px]

          border

          border-white/40

          bg-white/90

          shadow-[0_4px_12px_rgba(0,0,0,0.12)]

        "

      >

        <Bot

        className="h-[11px] w-[11px] text-[#075B59]"

        strokeWidth={1.8}

      />

      </motion.div>



      {/* AI Core */}

      <motion.div

        animate={{

          y: [0, -2, 0],

        }}

        transition={{

          duration: 4,

          repeat: Infinity,

          ease: "easeInOut",

        }}

        className="

          relative

          flex

          h-[58px]

          w-[58px]

          items-center

          justify-center

          rounded-[17px]

          border

          border-white/50

          bg-gradient-to-br

          from-[#bafff5]

          via-[#65e3d7]

          to-[#27b9ae]

        "

        style={{

          boxShadow: "0 10px 25px rgba(0,0,0,0.20)",

        }}

      >

        <div className="flex h-[40px] w-[40px] items-center justify-center rounded-[12px] bg-[#063a3e]">

          <Sparkles

            className="h-[19px] w-[19px] text-[#7bf3e7]"

            strokeWidth={1.4}

          />

        </div>

      </motion.div>

    </div>

  </div>

</motion.header>



          {/* Settings cards */}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

            <SettingsCard

              index={0}

              icon={Wrench}

              testId="bs-card-services"

              eyebrow="Catalog"

              title="Services"

              description="Define every service you offer — name, duration, price. These are used across staff and appointments."

              stat={svcCount === null ? "—" : `${svcActiveCount ?? 0}`}

              statLabel={

                svcCount === null

                  ? ""

                  : `Active service${

                      svcActiveCount === 1 ? "" : "s"

                    }${svcCount != null ? ` · ${svcCount} total` : ""}`

              }

              onClick={() => nav("/business-settings/services")}

            />



            <SettingsCard

              index={1}

              icon={Users2}

              testId="bs-card-team"

              eyebrow="Roster"

              title="Team Members"

              description="Add the people who deliver your services. Nexa uses this roster for appointment assignment and availability."

              stat={teamCount === null ? "—" : `${activeCount ?? 0}`}

              statLabel={

                teamCount === null

                  ? ""

                  : `Active staff${

                      teamCount != null ? ` · ${teamCount} total` : ""

                    }`

              }

              onClick={() => nav("/business-settings/team")}

            />



            <SettingsCard

              index={2}

              icon={Bot}

              testId="bs-card-automation"

              eyebrow="Automation"

              title="Automation"

              description="Control which WhatsApp notifications Nexa sends automatically — reminders, nudges and confirmations."

              stat={autoRules === null ? "—" : `${autoRules}`}

              statLabel={

                autoRules === null

                  ? ""

                  : `Active rule${autoRules === 1 ? "" : "s"}`

              }

              onClick={() => nav("/business-settings/automation")}

            />

          </div>



          {/* Business profile */}

              <motion.div

  initial={{ opacity: 0, y: 8 }}

  animate={{ opacity: 1, y: 0 }}

  transition={{ duration: 0.4, delay: 0.25 }}

  className="

    mt-4

    flex

    items-center

    justify-between

    rounded-[18px]

    border

    border-[#DCE7E1]

    bg-gradient-to-r from-white via-white to-[#EDF7F1]

    px-5

    py-4

    shadow-[0_7px_24px_rgba(11,37,40,0.065)]

    transition-all

    duration-300

    hover:border-[#B8DCD0]

    hover:shadow-[0_12px_28px_rgba(7,91,89,0.12)]

  "

>

  <div className="flex items-center gap-4">

    <div

      className="

        flex

        h-11

        w-11

        shrink-0

        items-center

        justify-center

        rounded-[13px]

        bg-gradient-to-br from-[#E5F3EC] to-[#D4E8DD]

        text-[#607572]

        transition-colors

        duration-300

      "

    >

      <Building2

        className="h-5 w-5"

        strokeWidth={1.7}

      />

    </div>



    <div>

      <div className="text-[14px] font-semibold tracking-[-0.01em] text-[#102C2D]">

        Business Profile

      </div>



      <div className="mt-1 text-[10px] text-[#82918C]">

        Keep your business information and operating details up-to-date.

      </div>

    </div>

  </div>



  <button

  type="button"

  onClick={() => nav("/business-settings/profile")}

  className="

  group

  flex

  items-center

  gap-1.5

  rounded-[9px]

  border

  border-[#075B59]

  bg-[#075B59]

  px-3

  py-2

  text-[9px]

  font-semibold

  text-white

  shadow-[0_5px_14px_rgba(7,91,89,0.18)]

  transition-all

  duration-300

  hover:bg-[#064B49]

  hover:border-[#064B49]

"

>

  Manage Profile



  <ArrowRight

    className="

      h-3 w-3

      transition-transform

      duration-300

      group-hover:translate-x-0.5

    "

    strokeWidth={1.8}

  />

</button>

</motion.div>



          {/* AI status */}

          <motion.div

            initial={{ opacity: 0 }}

            animate={{ opacity: 1 }}

            transition={{ duration: 0.4, delay: 0.35 }}

            className="

              mt-3

              flex

              items-center

              justify-center

              gap-2

              text-[9px]

              text-[#82918C]

            "

          >

            <CheckCircle2

              className="h-3.5 w-3.5 text-emerald-500"

              strokeWidth={1.8}

            />



            <span>

              Nexa AI is connected and ready to automate your business

              workflows.

            </span>

          </motion.div>

        </div>

      </main>

    </div>

  );

}